import { useCallback, useMemo, useState } from "react";
import * as React from "react";

import FaxForm from "../components/fax-form";
import { UPLOAD_SOURCE } from "../components/fax-form/fax-form.component";
import { Patient } from "../services/types/patients.type";

type FaxProviderProps = {
  children?: React.ReactNode;
};

const FaxContext = React.createContext<any>(null);

export function FaxProvider({ children }: FaxProviderProps) {
  const [openModal, setOpenModal] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [initialDocuments, setInitialDocuments] = useState<File[]>([]);
  const [source, setSource] = useState<string>(UPLOAD_SOURCE);

  const handleOpen = useCallback((patient?: Patient) => {
    setPatient(patient || null);
    setOpenModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setPatient(null);
    setInitialDocuments([]);
  }, []);

  const value = useMemo(
    () => ({
      handleOpen,
      selectedFiles,
      setSelectedFiles,
      setSource,
      setInitialDocuments
    }),
    [
      handleOpen,
      selectedFiles,
      setSelectedFiles,
      setSource,
      setInitialDocuments
    ]
  );

  return (
    <FaxContext.Provider value={value}>
      {children}
      <FaxForm
        isOpen={openModal}
        onClose={handleClose}
        patient={patient || undefined}
        selectedFiles={selectedFiles}
        initialDocuments={initialDocuments}
        source={source}
      />
    </FaxContext.Provider>
  );
}

export default function useFax() {
  const faxContext = React.useContext(FaxContext);
  if (!faxContext) {
    throw new Error(`useFax must be used within a FaxProvider`);
  }
  const {
    handleOpen,
    selectedFiles,
    setSelectedFiles,
    setSource,
    setInitialDocuments
  } = faxContext;
  return {
    handleOpen,
    selectedFiles,
    setSelectedFiles,
    setSource,
    setInitialDocuments
  };
}
